import type { LDMultiKindContext } from '@launchdarkly/node-server-sdk';

type PrimitiveKeyValue = Record<string, string | number | boolean | symbol>;

type UserContextInput = {
  userId: string;
  email?: string;
} & PrimitiveKeyValue;

type OrganizationContextInput = {
  organizationId: string;
  // Optional: LaunchDarkly targets on `key` and custom attributes, never on `name`, so this is a
  // dashboard label only. Backend jobs reach for flags with no org name in hand (an `AuthContext`
  // carries only the id) and must not pay a lookup just to fill it in.
  organizationName?: string;
} & PrimitiveKeyValue;

export const ContextType = Object.freeze({
  namespace: 'namespace',
  organization: 'organization',
  user: 'user',
});

/**
 * Every context includes the namespace kind, because LaunchDarkly targeting rules
 * are per-kind: a context without it silently misses every namespace-targeted rule.
 */
export const createContextFactory = ({
  namespace,
  ...globalExtras
}: { namespace: string } & PrimitiveKeyValue) => {
  const namespaceAttributes = () => ({
    ...globalExtras,
    key: namespace,
    namespace,
  });

  const organizationAttributes = ({
    organizationId,
    organizationName,
    ...extras
  }: OrganizationContextInput & PrimitiveKeyValue) => ({
    key: organizationId,
    ...(organizationName ? { name: organizationName } : {}),
    ...globalExtras,
    ...extras,
  });

  const userAttributes = ({ userId, email, ...extras }: PrimitiveKeyValue & UserContextInput) => ({
    key: userId,
    email,
    ...globalExtras,
    ...extras,
  });

  const multiContext = ({
    userInput,
    organizationInput,
  }: {
    userInput?: UserContextInput;
    organizationInput?: OrganizationContextInput;
  } = {}): LDMultiKindContext => ({
    kind: 'multi',
    ...(userInput ? { [ContextType.user]: userAttributes(userInput) } : {}),
    ...(organizationInput
      ? { [ContextType.organization]: organizationAttributes(organizationInput) }
      : {}),
    [ContextType.namespace]: namespaceAttributes(),
  });

  return {
    organizationContext: (input: OrganizationContextInput): LDMultiKindContext =>
      multiContext({ organizationInput: input }),
    userContext: (input: UserContextInput): LDMultiKindContext =>
      multiContext({ userInput: input }),
    multiContext,
  };
};
